import { useEffect, useState } from 'react';
import {
  Card,
  Group,
  Text,
  Badge,
  Stack,
  TextInput,
  Select,
  Grid,
  Button,
  LoadingOverlay,
  Alert,
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconAlertCircle } from '@tabler/icons-react';
import { useOrdersStore, fetchOrders } from '../store/orders';
import { OrderWithDetails } from '../types';

export function OrdersList() {
  const navigate = useNavigate();
  const { orders, filters, isLoading, error, setFilters } = useOrdersStore();
  const [statusOptions, setStatusOptions] = useState([]);
  const [fnoOptions, setFnoOptions] = useState([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch initial orders
    fetchOrders(filters);

    // Fetch status options
    const fetchStatusOptions = async () => {
      try {
        const res = await fetch('/api/statuses');
        if (!res.ok) throw new Error('Failed to fetch status options');
        const { data } = await res.json();
        setStatusOptions(
          data.map((status: any) => ({
            value: status.id.toString(),
            label: status.name,
          }))
        );
      } catch (err) {
        setOptionsError('Failed to load status options');
        console.error('Status options error:', err);
      }
    };

    // Fetch FNO options
    const fetchFnoOptions = async () => {
      try {
        const res = await fetch('/api/fnos');
        if (!res.ok) throw new Error('Failed to fetch FNO options');
        const { data } = await res.json();
        setFnoOptions(
          data.map((fno: any) => ({
            value: fno.id.toString(),
            label: fno.name,
          }))
        );
      } catch (err) {
        setOptionsError('Failed to load FNO options');
        console.error('FNO options error:', err);
      }
    };

    fetchStatusOptions();
    fetchFnoOptions();
  }, []);

  // Fetch orders when filters change
  useEffect(() => {
    fetchOrders(filters);
  }, [filters]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
      case 'new order':
        return 'blue';
      case 'in progress':
        return 'yellow';
      case 'installation scheduled':
        return 'cyan';
      case 'installed':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <Stack>
      <Card>
        <Grid style={{ alignItems: 'flex-end' }} gutter="md">
          <Grid.Col span={4}>
            <TextInput
              label="Search Orders"
              placeholder="Search by reference, client, or address"
              leftSection={<IconSearch size={16} />}
              value={filters.search || ''}
              onChange={(e) =>
                setFilters({ ...filters, search: e.currentTarget.value })
              }
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Select
              label="Filter by Status"
              placeholder="All Statuses"
              clearable
              data={statusOptions}
              value={filters.status_id?.toString() || null}
              onChange={(value) =>
                setFilters({
                  ...filters,
                  status_id: value ? parseInt(value) : undefined,
                })
              }
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Select
              label="Filter by FNO"
              placeholder="All FNOs"
              clearable
              data={fnoOptions}
              value={filters.fno_id?.toString() || null}
              onChange={(value) =>
                setFilters({
                  ...filters,
                  fno_id: value ? parseInt(value) : undefined,
                })
              }
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <Button
              variant="outline"
              onClick={() => setFilters({})}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid.Col>
        </Grid>
      </Card>

      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        {(error || optionsError) && (
          <Alert 
            icon={<IconAlertCircle size={16} />} 
            color="red" 
            title="Error" 
            mb="md"
          >
            {error || optionsError}
          </Alert>
        )}
        <Stack>
          {!isLoading && !error && orders.length === 0 && (
            <Card>
            <Text ta="center" color="dimmed">No orders found</Text>
            </Card>
          )}
          {orders.map((order: OrderWithDetails) => (
            <Card
              key={order.id}
              onClick={() => navigate(`/orders/${order.id}`)}
              style={{
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <Group>
                    <Text style={{ fontWeight: 500 }}>{order.reference_number}</Text>
                    <Badge
                      color={getStatusColor(order.current_status.name)}
                      variant="light"
                    >
                      {order.current_status.name}
                    </Badge>
                  </Group>
                  <Text size="sm" color="dimmed" mt={4}>
                    {order.client.name}
                  </Text>
                  <Text size="sm" mt={4}>
                    {order.address.street_address}, {order.address.suburb}
                  </Text>
                </div>
                <div>
                  <Text size="sm" style={{ fontWeight: 500, textAlign: 'right' }}>
                    {order.package_name}
                  </Text>
                  <Text size="xs" color="dimmed" style={{ textAlign: 'right' }} mt={4}>
                    {order.fiber_network_operator.name}
                  </Text>
                </div>
              </div>
            </Card>
          ))}
        </Stack>
      </div>
    </Stack>
  );
}
